import { Modal } from "@/Common/Components/Modals/Modal";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { IObstetricalFilePageProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/ObstetricalFilePage";
import { OntarioObstetricalFilePage, OntarioObstetricalFilePageState } from "@/Patient/Components/MedicalNote/Obstetric/Form/Ontario/OntarioObstetricalFilePage";
import { ObstetricalFileContributionType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileContributionType";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { IssueAndPlanOfManagement } from "@/Patient/Models/ObstetricalFile/Ontario/OPR3/IssueAndPlanOfManagement";
import { OntarioObstetricalRecommendedImmuniprophylaxis } from "@/Patient/Models/ObstetricalFile/Ontario/OPR3/OntarioObstetricalRecommendedImmuniprophylaxis";
import { OntarioObstetricalVisit } from "@/Patient/Models/ObstetricalFile/Ontario/OPR3/OntarioObstetricalVisit";
import { PrenatalVisitInitials } from "@/Patient/Models/ObstetricalFile/PrenatalVisitInitials";
import { OntarioObstetricalFileDiscussionTopics } from "./Subcomponent/OntarioObstetricalFileDiscussionTopics";
import { OntarioObstetricalFileIssuesPlanOfManagement } from "./Subcomponent/OntarioObstetricalFileIssuesPlanOfManagement";
import { OntarioObstetricalFileRecommendedImmuniprophylaxis } from "./Subcomponent/OntarioObstetricalFileRecommendedImmuniprophylaxis";
import { OntarioObstetricalFileSpecialCircumstancesGbs } from "./Subcomponent/OntarioObstetricalFileSpecialCircumstancesGbs";
import { OntarioObstetricalFileSubsequentVisitsTable } from "./Subcomponent/OntarioObstetricalFileSubsequentVisitsTable";

class OntarioObstetricalFilePageThreeState extends OntarioObstetricalFilePageState {
    public issueAndPlanOfManagementRows: IssueAndPlanOfManagement[];
    public currentEditSubsequentVisitRowId: number;
    public isDiscussionTopicsCommentsClickableTextShown: boolean;
    public isOtherPostPartumVaccineDiscussedClickableTextShown: boolean;
    public isReuseVitalSignPopupShown: boolean;
    public currentVisitId: number;
    public heightInMeters: number;
}

class OntarioObstetricalFileOpr3 extends OntarioObstetricalFilePage<OntarioObstetricalFilePageThreeState> {
    private readonly componentId = "ontario-obstetrical-file-OPR3";
    private newEmrTemplateId: string;

    public componentDidMount(): void {
        super.componentDidMount();
        this.checkReuseVitalSign();

        if (this.newEmrTemplateId) {
            this.handleObstetricalFileVitalSignLink(this.newEmrTemplateId);
        }
    }

    public componentDidUpdate(prevProps: IObstetricalFilePageProps<OntarioObstetricalFileForm>): void {
        super.componentDidUpdate(prevProps);
        if (this.props.readonly !== prevProps.readonly)
            this.checkReuseVitalSign();
    }

    protected buildState(props: IObstetricalFilePageProps<OntarioObstetricalFileForm>): OntarioObstetricalFilePageThreeState {
        const state = new OntarioObstetricalFilePageThreeState();
        state.form = new OntarioObstetricalFileForm(props.form);
        this.setRecommendedImmunoprophylaxis(state.form.Opr3.RecommendedImmuniprophylaxis);
        state.isDiscussionTopicsCommentsClickableTextShown = false;

        const currentVisit = state.form.Opr3.SubsequentVisits.firstOrDefault(v => v.ContributionId === this.props.contributionId);
        const emrTemplateIdBefore = currentVisit?.EmrTemplateId;

        state.form.setSubsequentVisitsVitalSigns(
            props.obstetricalFileMedicalSummary
            , props.encounter.ConsultationDate
            , props.contributionCreationDate
            , props.contributionId
        );

        this.newEmrTemplateId = currentVisit?.EmrTemplateId !== emrTemplateIdBefore ? currentVisit?.EmrTemplateId : null;

        state.heightInMeters = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
            obstetricalFileMedicalSummary: props.obstetricalFileMedicalSummary,
            emrTemplateId: state.form.Opr2.VitalSignEmrTemplateId,
            consultationDate: props.encounter.ConsultationDate,
            obstetricalFileContributionDate: props.contributionCreationDate,
            skipLoadLastHour: !!state.form.Opr2.VitalSignEmrTemplateId
        })?.heightInMeters;

        return state;
    }

    protected renderPageContent(): JSX.Element {
        return (
            <div id={this.componentId} className="inner-border">
                {this.renderPersonalInfoAndObstetricalCountersSection()}
                <OntarioObstetricalFileIssuesPlanOfManagement {...this.getSubcomponentBaseProps()} />
                <OntarioObstetricalFileSpecialCircumstancesGbs {...this.getSubcomponentBaseProps()} />
                <OntarioObstetricalFileRecommendedImmuniprophylaxis {...this.getSubcomponentBaseProps()} />
                {this.renderSubsequentVisitsSection()}
                <OntarioObstetricalFileDiscussionTopics {...this.getSubcomponentBaseProps()} />
                {this.renderInitialsMappingSection()}
            </div>
        );
    }

    protected async onVitalSignUpdated(): Promise<void> {
        if (IsNullOrUndefined(this.state.currentVisitId))
            return;

        const visit = this.state.form.Opr3.SubsequentVisits.firstOrDefault(p => p.getId() === this.state.currentVisitId);

        if (!visit)
            return;

        const updatedVitalSign = await super.handleVitalSignUpdate(
            visit.vitalSign,
            visit.EmrTemplateId
        );

        this.mutateState(
            s => s.form.Opr3.SubsequentVisits.mutate(
                item => {
                    item.updateVitalSignValueFromMedicalItem(updatedVitalSign);
                    item.EmrTemplateId = updatedVitalSign?.EMRTemplateId;
                },
                item => item.getId() === this.state.currentVisitId
            ),
            () => this.handleObstetricalFileVitalSignLink(updatedVitalSign?.EMRTemplateId)
        );
    }

    private renderPersonalInfoAndObstetricalCountersSection(): JSX.Element {
        return (
            <div className="section">
                <div className="row row-form">
                    {this.renderLastName("col-md-3")}
                    {this.renderFirstName("col-md-3")}
                </div>
                <div className="row row-form">
                    {this.renderPlannedBirthAttendant("col-md-4")}
                    {this.renderNewBornFields("col-md-7")}
                </div>
                <div className="row row-form">
                    {this.renderFamilyPhysicianOrPrimaryCareProvider("col-md-4")}
                    {this.renderAllergiesOrSensitivites("col-md-7")}
                </div>
                <div className="row row-form">
                    {this.renderMedications("col-md-11")}
                </div>
                <div className="row row-form">
                    {this.renderObstetricalCounters("col-md-7")}
                    {this.renderFinalEstimatedBirthDate("OPR2-FinalEdb", "col-md-2")}
                </div>
            </div>
        );
    }

    private renderSubsequentVisitsSection(): JSX.Element {
        return (
            <div className="section border border-top subsequent-visits">
                <OntarioObstetricalFileSubsequentVisitsTable
                    {...this.getSubcomponentBaseProps()}
                    visits={this.state.form.Opr3.SubsequentVisits}
                    onChange={(visits: OntarioObstetricalVisit[], currentVisitChange: OntarioObstetricalVisit, newInitialsMapping: string, shouldHandleFormUpdate: boolean) =>
                        this.onSubsequentVisitsTableChange(visits, currentVisitChange, newInitialsMapping, shouldHandleFormUpdate)
                    }
                    onWeightUnitChange={unit => this.mutateModelStateWithFormUpdate(s => s.form.Metadata.subsequentVisitsTableWeightUnit = unit)}
                    onVitalSignUpdated={() => this.onVitalSignUpdated()}
                    onAddSubsequentVisitRow={!this.props.readonly
                        && this.props.contributionType !== ObstetricalFileContributionType.Postnatal
                        && !this.contributionHasSubsequentVisitRow()
                        ? () => this.addSubsequentVisitRow(this.props)
                        : null
                    }
                    vitalSignControlRenderer={this.vitalSignControlRenderer}
                    existingInitials={this.existingInitials}
                    heightInMeters={this.state.heightInMeters}
                    obstetricalFileMedicalSummary={this.props.obstetricalFileMedicalSummary}
                />
                {this.state.isReuseVitalSignPopupShown && this.renderVitalSignOptionPopup()}
            </div>
        );
    }

    private renderInitialsMappingSection(): JSX.Element {
        return (
            <div className="section initials-mapping">
                <div className="row row-form flex-container">
                    {this.state.form.Opr3.InitialsMapping.map((initialsAndName, index) => (
                        // Exceptionnaly using index as key since it should never change here.
                        <div className="col-md-2 initials-field-wrapper" key={index}>
                            {this.renderField(
                                null,
                                this.controlRenderer.renderTextbox({
                                    value: initialsAndName,
                                    onChange: value => this.mutateModelState(s => s.form.Opr3.InitialsMapping[index] = value),
                                    maxLength: 35
                                }),
                                null,
                                null,
                                `${index + 1}. ${cacheStore.GetString("ObstetricalFile_NameInitialsLabel", null, this.locale)}`
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    private renderVitalSignOptionPopup(): JSX.Element {
        const footer = (
            <div className="modal-footer">
                <button
                    className="btn btn-default"
                    onClick={() => this.closeReuseVitalSignPopup(null, () => this.handleFormUpdate())}
                >
                    {cacheStore.GetString("NewVitalSignsLabel")}
                </button>
                <button
                    className="btn btn-primary"
                    onClick={() => this.onReuseVitalSign()}
                >
                    {cacheStore.GetString("ReuseLabel")}
                </button>
            </div>
        );

        return (
            <Modal
                title={cacheStore.GetString("ObstetricalFile_ReusingVitalSignsTitleLabel")}
                showOverlay
                disableOverlayClose
                roundTopCorner
                footer={footer}
                onClose={() => this.mutateState(s => s.isReuseVitalSignPopupShown = false)}
            >
                <div>
                    {cacheStore.GetString("ObstetricalFile_ReusingVitalSignsQuestionONLabel")}
                </div>
            </Modal>
        );
    }

    private setRecommendedImmunoprophylaxis(recommended: OntarioObstetricalRecommendedImmuniprophylaxis): void {
        if (this.props.obstetricalFileMedicalSummary.hasInfluenzaImmunization(
            this.props.encounter.ConsultationDate,
            this.props.consultationDateAtStartOfObstetricalFile
        ))
            recommended.IsInfluenzaVaccineReceived ??= true;

        if (this.props.obstetricalFileMedicalSummary.hasPertussisImmunization(
            this.props.encounter.ConsultationDate,
            this.props.consultationDateAtStartOfObstetricalFile
        ))
            recommended.IsPertussisVaccineReceived ??= true;
    }

    private checkReuseVitalSign(): void {
        if (!this.props.readonly
            && !this.state.form.Metadata.isReuseVitalSignOptionSet
            && !!this.state.form.Opr2.VitalSignEmrTemplateId
            && this.state.form.Opr3.SubsequentVisits.any()
            && !this.state.form.Opr3.SubsequentVisits[0].EmrTemplateId
            && this.isValidVitalSignForVisit()) {
            this.mutateState(s => {
                s.form.Metadata.isReuseVitalSignOptionSet = true;
                s.isReuseVitalSignPopupShown = true;
            });
        }
    }

    private closeReuseVitalSignPopup(mutation?: (state: OntarioObstetricalFilePageThreeState) => void, then?: () => void): void {
        this.mutateState(
            s => {
                s.isReuseVitalSignPopupShown = false;
                mutation?.(s);
            },
            then
        );
    }

    private onReuseVitalSign(): void {
        this.closeReuseVitalSignPopup(
            s => {
                const firstVisit = s.form.Opr3.SubsequentVisits[0];
                const { obstetricalFileMedicalSummary, encounter, contributionCreationDate } = this.props;
                const emrTemplateId = s.form.Opr2.VitalSignEmrTemplateId;
                s.currentVisitId = firstVisit.getId();

                const vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                    obstetricalFileMedicalSummary: obstetricalFileMedicalSummary,
                    emrTemplateId: emrTemplateId,
                    consultationDate: encounter.ConsultationDate,
                    obstetricalFileContributionDate: contributionCreationDate
                });
                firstVisit.vitalSign = vitalSign;

                if (!firstVisit.Initials)
                    firstVisit.Initials = new PrenatalVisitInitials();

                firstVisit.Initials.addInitialsFromUser(sessionStore.getCurrentEmployee(), this.existingInitials, ", ");
            },
            () => this.onVitalSignUpdated()
        );
    }

    private isValidVitalSignForVisit(): boolean {
        const { obstetricalFileMedicalSummary, encounter } = this.props;

        const vitalSign = obstetricalFileMedicalSummary.getVitalSignBy(this.state.form.Opr2.VitalSignEmrTemplateId, encounter.ConsultationDate);

        return vitalSign
            && (vitalSign.Weight || vitalSign.BPDia || vitalSign.BPSys)
            && LuxonDateTimeParser.parse(vitalSign.PharmaStartDate).hasSame(LuxonDateTimeParser.parse(encounter.ConsultationDate), "day");
    }

    private onSubsequentVisitsTableChange(visits: OntarioObstetricalVisit[], currentVisitChange: OntarioObstetricalVisit, newInitialsMapping: string, shouldHandleFormUpdate: boolean): void {
        this.mutateModelState(
            s => {
                s.form.Opr3.SubsequentVisits = visits;
                s.currentVisitId = currentVisitChange.getId();

                s.form.Opr3.InitialsMapping = s.form.Opr3.InitialsMapping.map(i => i.trim());
                let firstEmptyIdx = s.form.Opr3.InitialsMapping.indexOf("");

                if (!IsNullOrWhiteSpace(newInitialsMapping) &&
                    firstEmptyIdx != -1 &&
                    !s.form.Opr3.InitialsMapping.contains(newInitialsMapping)
                ) {
                    s.form.Opr3.InitialsMapping[firstEmptyIdx] = newInitialsMapping;
                }
            },
            () => {
                if (shouldHandleFormUpdate)
                    this.handleFormUpdate?.();
            }
        );
    }

    private contributionHasSubsequentVisitRow(): boolean {
        return this.state.form.Opr3.SubsequentVisits.any(visit => visit.ContributionId === this.props.contributionId);
    }

    private get existingInitials(): PrenatalVisitInitials[] {
        return this.state.form.Opr3.SubsequentVisits.map(v => v.Initials);
    }
}

export { OntarioObstetricalFileOpr3 };
